import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { abbrevNum } from "../lib/elementStyle";
import { Link2, Loader2, Eye, ThumbsUp, Users, PlayCircle, Trash2, KeyRound, AlertTriangle } from "lucide-react";
import { YouTubeIcon } from "./YouTubeIcon";
import { t } from "../i18n";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 bg-black/40 border border-border rounded-sm px-3 py-2">
      <Icon className="h-4 w-4 text-red-500" />
      <div className="flex flex-col">
        <span className="text-[10px] text-muted-foreground">{label}</span>
        <span className="text-sm font-mono font-bold" data-testid={`yt-stat-${label}`}>{value}</span>
      </div>
    </div>
  );
}

export function YouTubePanel({ open, onOpenChange, overlayId, config, setConfig, data, setData }) {
  const navigate = useNavigate();
  const [kstatus, setKstatus] = useState(null); // {has_key, status}
  const [urlInput, setUrlInput] = useState(config?.url || "");
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/youtube/status").then(({ data: s }) => setKstatus(s)).catch(() => setKstatus({ has_key: false, status: "nao_configurada" }));
  }, [open]);

  const connect = async () => {
    if (!urlInput.trim()) return;
    setConnecting(true);
    try {
      const { data: r } = await api.post(`/overlays/${overlayId}/youtube/connect`, { url: urlInput.trim() });
      setConfig(r.youtube); setData(r.data);
      toast.success(t("Transmissão conectada"));
    } catch (e) { toast.error(e.response?.data?.detail || t("Falha ao conectar")); }
    finally { setConnecting(false); }
  };

  const disconnect = async () => {
    try {
      await api.post(`/overlays/${overlayId}/youtube/disconnect`);
      setConfig({ connected: false }); setData({ connected: false });
      toast.success(t("Transmissão desconectada"));
    } catch { toast.error(t("Falha ao desconectar")); }
  };

  const connected = config?.connected;
  const keyOk = kstatus?.has_key && kstatus?.status !== "invalida";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="youtube-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <YouTubeIcon /> {t("Dados do YouTube ao vivo")}
          </DialogTitle>
          <DialogDescription>
            {t("Conecte sua transmissão para usar espectadores, curtidas e chat ao vivo nos elementos do overlay.")}
          </DialogDescription>
        </DialogHeader>

        {kstatus === null ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : !keyOk ? (
          <div className="border border-amber-500/40 bg-amber-500/10 rounded-md p-3 space-y-2" data-testid="yt-no-key-notice">
            <p className="text-xs text-amber-300 flex items-start gap-1.5">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {kstatus.status === "invalida"
                ? t("Sua chave do YouTube está inválida (pode ter sido revogada). Atualize a chave para continuar.")
                : t("Você ainda não conectou uma chave do YouTube na sua conta.")}
            </p>
            <Button size="sm" className="w-full rounded-sm h-8" onClick={() => { onOpenChange(false); navigate("/profile?tab=youtube"); }} data-testid="yt-configure-now-btn">
              <KeyRound className="h-3.5 w-3.5 mr-1.5" /> {t("Configurar agora")}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> {t("URL ou ID do vídeo/transmissão")}</label>
            <div className="flex gap-2">
              <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="https://youtube.com/watch?v=..."
                onKeyDown={(e) => { if (e.key === "Enter") connect(); }}
                className="flex-1 h-9 bg-input border border-border rounded-sm px-2 text-xs outline-none focus:border-primary" data-testid="yt-url-input" />
              <Button onClick={connect} disabled={connecting} size="sm" className="rounded-sm h-9" data-testid="yt-connect-btn">
                {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Conectar")}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">{t("A chave da sua conta é usada automaticamente — não precisa configurar nada por overlay.")}</p>
          </div>
        )}

        {connected && (
          <div className="space-y-2 pt-1" data-testid="yt-live-preview">
            <div className="flex items-center justify-between">
              <span className="text-xs truncate max-w-[300px]">{data?.title || config?.title || t("Transmissão")}</span>
              <span className={`text-[10px] flex items-center gap-1 ${data?.isLive ? "text-red-500" : "text-muted-foreground"}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${data?.isLive ? "bg-red-500 animate-pulse" : "bg-muted-foreground"}`} /> {data?.isLive ? t("AO VIVO") : "offline"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Stat icon={Eye} label={t("Espectadores")} value={abbrevNum(data?.viewers || 0)} />
              <Stat icon={ThumbsUp} label={t("Curtidas")} value={abbrevNum(data?.likes || 0)} />
              <Stat icon={Users} label={t("Inscritos")} value={abbrevNum(data?.subscribers || 0)} />
              <Stat icon={PlayCircle} label={t("Visualizações")} value={abbrevNum(data?.views || 0)} />
            </div>
            <Button onClick={disconnect} variant="destructive" size="sm" className="w-full rounded-sm h-8" data-testid="yt-disconnect-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t("Desconectar transmissão")}
            </Button>
            <p className="text-[10px] text-muted-foreground">{t("Os dados atualizam automaticamente e refletem em tempo real nos elementos dinâmicos do overlay.")}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
